import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import { format, subDays, addDays } from 'date-fns';
import { FaBed, FaEdit } from 'react-icons/fa';
import './DailyReportsPage.css'; // Подключаем стили
import Toast from '../Toast';
import EditReportModal from '../EditReportModal';
import { getAllFactories, getPreviousDayReport, getDailyReportsWithAllFactories, saveFactoryPlan,
            getFactoryPlansForMonth, downloadDailyReport, saveFactorySleepingPlaces } from '../../api';  // Импортируем функции из api.js
import { colorOptionsFind } from '../references/ColorOptions';
import {
    buildAverageCumulativeRevenueTooltip,
    getRevenuePeriodSummary,
    REVENUE_HEADER_TOOLTIP
} from './revenuePeriodUtils';

const DailyReportsPage = () => {
    const location = useLocation();
    const [factories, setFactories] = useState([]);
    const [selectedFactories, setSelectedFactories] = useState([]);
    const [date, setDate] = useState(format(subDays(new Date(), 0), 'yyyy-MM-dd')); // инициализированная дата
    const [reports, setReports] = useState([]);
    const [toastMessage, setToastMessage] = useState(null);
    const [toastType, setToastType] = useState(''); // success или error
    const [isExporting, setIsExporting] = useState(false); // Отслеживаем процесс выгрузки
    const [previousDayTotals, setPreviousDayTotals] = useState(null); // Для хранения итогов за вчера
    const [hideNoReports, setHideNoReports] = useState(false); // Состояние чекбокса для скрытия строк без отчетов
    const [plans, setPlans] = useState({}); // Состояние для хранения планов по каждому заводу
    const [editingFactoryId, setEditingFactoryId] = useState(null); // Состояние для отслеживания редактируемой ячейки
    const [sleepingPlaces, setSleepingPlaces] = useState({});
    const [editingSleepingPlacesFactoryId, setEditingSleepingPlacesFactoryId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isInitializedFromQuery, setIsInitializedFromQuery] = useState(false);
    const [editingReport, setEditingReport] = useState(null); // Состояние для редактируемого отчета



    // Получаем заводы при загрузке страницы
    useEffect(() => {
        getAllFactories()
            .then(response => setFactories(response.data))
            .catch(error => {
                setToastMessage(error.response?.data?.message || 'Ошибка при загрузке заводов: ' + error.message);
                setToastType('error');
            });
    }, []);

    useEffect(() => {
        if (!factories.length) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const factoryIdParam = searchParams.get('factoryId');
        const dateParam = searchParams.get('date');

        if (factoryIdParam) {
            const factory = factories.find(
                (f) => String(f.id) === String(factoryIdParam)
            );

            if (factory) {
                setSelectedFactories([
                    {
                        value: factory.id,
                        label: factory.name,
                    },
                ]);
            }
        }

        if (dateParam) {
            setDate(dateParam);
        }

        setIsInitializedFromQuery(true);
    }, [factories, location.search]);

    // Получаем отчеты при изменении выбранных заводов или даты
    useEffect(() => {
            const searchParams = new URLSearchParams(location.search);
            const hasQueryFilters = !!(searchParams.get('factoryId') || searchParams.get('date'));

            if (hasQueryFilters && !isInitializedFromQuery) {
                return;
            }

            const factoryIds = selectedFactories.map(factory => factory.value);

            const month = format(new Date(date), 'yyyy-MM');  // Извлекаем месяц из выбранной даты

            getDailyReportsWithAllFactories(factoryIds, date)
                .then(response => {
                    setReports(response.data);
                    const newSleepingPlaces = {};
                    response.data.forEach(report => {
                        newSleepingPlaces[report.factory.id] = report.sleepingPlaces ?? '';
                    });
                    setSleepingPlaces(newSleepingPlaces);
                })
                .catch(error => {
                    setToastMessage(error.response?.data?.message || 'Ошибка при загрузке отчетов: ' + error.message);
                    setToastType('error');
                    console.error('Ошибка при загрузке отчетов:', error);
                });

            // Запрашиваем планы на выбранный месяц
            getFactoryPlansForMonth(factoryIds, month)
                .then(response => {
                                    const newPlans = {};
                                    response.data.forEach(plan => {
                                        newPlans[plan.factory.id] = plan.plan || '';  // Если план пуст, сохраняем как пустую строку
                                    });
                                    setPlans(newPlans);
                })
                .catch(error => {
                                    setToastMessage(error.response?.data?.message || 'Ошибка при загрузке планов: ' + error.message);
                                    setToastType('error');
                });
    }, [selectedFactories, date, isInitializedFromQuery, location.search]);



    // Получаем итоги за предыдущие дни
    useEffect(() => {
        const fetchPreviousDayTotals = async () => {
            try {
                const factoryIds = selectedFactories.map(factory => factory.value);

                const response = await getPreviousDayReport(factoryIds, date);
                if (response.data) {
                    setPreviousDayTotals(response.data); // Сохраняем итоги за найденный день
                } else {
                    setPreviousDayTotals(null); // Если нет данных, сбрасываем
                }
            } catch (error) {
                console.error('Ошибка при загрузке отчетов за предыдущий день:', error);
            }
        };

        fetchPreviousDayTotals();
    }, [selectedFactories, date]);


    const sortedReports = React.useMemo(() => {
        let sortableReports = [...reports];
        if (sortConfig.key !== null) {
            sortableReports.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableReports;
    }, [reports, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };




    // Сохраняем план на бэкенд
    const handlePlanChange = async (factoryId, plan) => {
        if (plan === undefined || plan === null) return;
        const month = format(new Date(date), 'yyyy-MM'); // Извлекаем месяц из выбранной даты
        try {
            await saveFactoryPlan(factoryId, month, plan);
            setToastMessage('План успешно сохранен');
            setToastType('success');
        } catch (error) {
            setToastMessage(error.response?.data?.message || 'Ошибка при сохранении плана: ' + error.message);
            setToastType('error');
        }
    };

    const factoryOptions = factories.map(factory => ({
        value: factory.id,
        label: factory.name
    }));

    const decrementDate = () => setDate(format(subDays(new Date(date), 1), 'yyyy-MM-dd'));
    const incrementDate = () => setDate(format(addDays(new Date(date), 1), 'yyyy-MM-dd'));



    // Обработчик изменения чекбокса
    const handleHideNoReportsChange = (e) => {
        setHideNoReports(e.target.checked);
    };




    const calculateTotals = () => {
        const totals = {
            atStationToday: 0,
            atStationMonthToDate: 0,
            averageCumulativeRevenue: 0,
            totalInCity: 0,
            newWorkersToday: 0,
            onMedicalToday: 0,
            leftFactoryToday: 0,
            onVacationToday: 0,
            requestToday: 0,
            ...getRevenuePeriodSummary(reports)
        };
        reports.forEach(report => {
            totals.atStationToday += report.atStationToday;
            totals.atStationMonthToDate += report.atStationMonthToDate || 0;
            totals.averageCumulativeRevenue += Number(report.averageCumulativeRevenue || 0);
            totals.totalInCity += report.totalInCity;
            totals.newWorkersToday += report.newWorkersToday;
            totals.onMedicalToday += report.onMedicalToday;
            totals.leftFactoryToday += report.leftFactoryToday;
            totals.onVacationToday += report.onVacationToday;
            totals.requestToday += report.requestToday;
        });
        return totals;
    };

    const totals = calculateTotals();
    const exportToExcel = async () => {
        setIsExporting(true); // Устанавливаем состояние загрузки
        try {
            const factoryIds = selectedFactories.map(factory => factory.value).join(',');  // Преобразуем массив в строку через запятую

            // Отправляем запрос на бэкенд для генерации отчета
            const response = await downloadDailyReport(factoryIds, date);

            // Создаем ссылку для скачивания файла
            const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Дневной_отчет_${date}.xlsx`;
            link.click();
            setToastMessage('Отчет успешно выгружен');
            setToastType('success');
        } catch (error) {
            setToastMessage('Ошибка при выгрузке отчета: ' + error.message);
            setToastType('error');
            console.error('Ошибка при выгрузке отчета:', error);
        } finally {
            setIsExporting(false); // Возвращаем кнопку в доступное состояние
        }
    };

    // Функция для форматирования чисел с символом рубля
    const formatRevenue = (value) => {
        if (value === null || value === undefined || value === 0) return '—';
        // Форматируем число без пробелов и добавляем символ рубля
        const formattedNumber = new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
        return formattedNumber + '₽';
    };

    const formatTextWithLineBreaks = (text) => {
        return text?.trim().split(/\r\n|\n|\r/)?.map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ));
    };

    // Функция для преобразования таймзоны в формат МСК+Ч (смещение относительно МСК)
    const formatTimeZone = (timeZone) => {
        if (!timeZone) return '';
        
        // Смещения относительно МСК (UTC+3)
        const timeZoneMap = {
            'Europe/Kaliningrad': 'МСК-1',    // UTC+2
            'Europe/Moscow': 'МСК',         // UTC+3
            'Europe/Samara': 'МСК+1',         // UTC+4
            'Europe/Saratov': 'МСК+1',         // UTC+4
            'Asia/Yekaterinburg': 'МСК+2',    // UTC+5
            'Asia/Omsk': 'МСК+3',             // UTC+6
            'Asia/Krasnoyarsk': 'МСК+4',      // UTC+7
            'Asia/Novosibirsk': 'МСК+4',      // UTC+7
            'Asia/Irkutsk': 'МСК+5',          // UTC+8
            'Asia/Yakutsk': 'МСК+6',          // UTC+9
            'Asia/Vladivostok': 'МСК+7',      // UTC+10
            'Asia/Magadan': 'МСК+8',          // UTC+11
            'Asia/Kamchatka': 'МСК+9',        // UTC+12
            'Asia/Anadyr': 'МСК+9'            // UTC+12
        };
        
        return timeZoneMap[timeZone] || timeZone.split('/').pop();
    };

    // Обработчик изменения значения плана
    const handlePlanInputChange = (factoryId, value) => {
        const updatedPlans = { ...plans, [factoryId]: value };
        setPlans(updatedPlans);
    };

    // Сохранение плана при потере фокуса или нажатии клавиши Enter
    const handleBlurOrEnter = (factoryId, value) => {
        handlePlanChange(factoryId, value); // Сохраняем изменения на бэкенде
        setEditingFactoryId(null); // Убираем режим редактирования
    };

    const handleKeyDown = (e, factoryId, value) => {
        if (e.key === 'Enter') {
            handleBlurOrEnter(factoryId, value); // Сохраняем при нажатии Enter
        }
        if (e.key === 'Escape') {
            setEditingFactoryId(null);
        }
    };

    const handleSleepingPlacesInputChange = (factoryId, value) => {
        setSleepingPlaces({ ...sleepingPlaces, [factoryId]: value });
    };

    const handleSleepingPlacesChange = async (factoryId, value) => {
        const isEmpty = value === undefined || value === null || value === '';

        if (!isEmpty) {
            const normalizedValue = Number(value);
            if (!Number.isInteger(normalizedValue) || normalizedValue < 0) {
                setToastMessage('Количество спальных мест должно быть целым неотрицательным числом');
                setToastType('error');
                return;
            }
        }

        const valueToSave = isEmpty ? null : Number(value);

        try {
            await saveFactorySleepingPlaces(factoryId, date, valueToSave);
            setReports(currentReports => currentReports.map(report => (
                report.factory.id === factoryId
                    ? { ...report, sleepingPlaces: valueToSave }
                    : report
            )));
            setSleepingPlaces(currentSleepingPlaces => ({
                ...currentSleepingPlaces,
                [factoryId]: valueToSave ?? '',
            }));
            setToastMessage(isEmpty ? 'Количество спальных мест сброшено' : 'Количество спальных мест сохранено');
            setToastType('success');
        } catch (error) {
            setToastMessage(error.response?.data?.message || 'Ошибка при сохранении спальных мест: ' + error.message);
            setToastType('error');
        } finally {
            setEditingSleepingPlacesFactoryId(null);
        }
    };

    const handleSleepingPlacesKeyDown = (e, factoryId, value) => {
        if (e.key === 'Enter') {
            handleSleepingPlacesChange(factoryId, value);
        }
        if (e.key === 'Escape') {
            setEditingSleepingPlacesFactoryId(null);
        }
    };

    const renderSleepingPlacesCell = (report) => {
        const factoryId = report.factory.id;

        return (
            <td
                className={`td_sleeping_places ${editingSleepingPlacesFactoryId === factoryId ? 'td_sleeping_places--editing' : ''}`}
                title="кол-во спальных мест"
                onClick={() => setEditingSleepingPlacesFactoryId(factoryId)}
            >
                {editingSleepingPlacesFactoryId === factoryId ? (
                    <input
                        type="number"
                        min="0"
                        value={sleepingPlaces[factoryId] ?? ''}
                        onChange={(e) => handleSleepingPlacesInputChange(factoryId, e.target.value)}
                        onBlur={() => handleSleepingPlacesChange(factoryId, sleepingPlaces[factoryId])}
                        onKeyDown={(e) => handleSleepingPlacesKeyDown(e, factoryId, sleepingPlaces[factoryId])}
                        autoFocus
                    />
                ) : (
                    sleepingPlaces[factoryId] !== undefined && sleepingPlaces[factoryId] !== null && sleepingPlaces[factoryId] !== ''
                        ? sleepingPlaces[factoryId]
                        : '—'
                )}
            </td>
        );
    };

    // Обработчики для модального окна редактирования отчета
    const handleEditReport = (report) => {
        setEditingReport(report);
    };

    const handleCloseEditModal = () => {
        setEditingReport(null);
    };

    const handleSaveReport = (updatedReport) => {
        const factoryIds = selectedFactories.map(factory => factory.value);
        
        getDailyReportsWithAllFactories(factoryIds, date)
            .then(response => {
                setReports(response.data);
                setToastMessage(updatedReport.id ? 'Отчет успешно обновлен' : 'Отчет успешно создан');
                setToastType('success');
            })
            .catch(error => {
                setToastMessage(error.response?.data?.message || 'Ошибка при загрузке отчетов: ' + error.message);
                setToastType('error');
            });
        
        setEditingReport(null);
    };

    return (
        <div className="daily-reports__page-container">
        <div className="content-container">
            <div className="daily-reports__filter-container">
                <div className="filter-group">
                    <Select
                        id="factories"
                        options={factoryOptions}
                        isMulti
                        value={selectedFactories}
                        onChange={setSelectedFactories}
                        placeholder="Выберите заводы"
                        className="factory-select"
                    />
                </div>
                <div className="date-controls">
                    <button className="date-button" onClick={decrementDate}>{"◀"}</button>
                    <input
                        id="date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="date-input"
                    />
                    <button className="date-button" onClick={incrementDate}>{"▶"}</button>
                </div>
                <div className="export-controls">
                    <button
                        className="export-button"
                        onClick={exportToExcel}
                        disabled={isExporting} // Дизейблим кнопку, если идет выгрузка
                    >
                        {isExporting ? 'Загрузка...' : 'Выгрузить в Excel'} {/* Меняем текст на кнопке */}
                    </button>

                </div>

                <div className="hide-no-reports-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={hideNoReports}
                                onChange={handleHideNoReportsChange}
                            />
                            Скрыть<br/>пустые
                        </label>
                    </div>
            </div>

            <div className="daily-reports__report-table">
            <div className="table-container">
                <table className="reports-table">
                <thead>
                    <tr>
                        <th className="td_idx"></th>
                        <th onClick={() => requestSort('factory.cluster.name')}>
                            Кластер
                        </th>
                        <th onClick={() => requestSort('factory.name')}>
                            Наименование предприятия
                        </th>
                        <th style={{ backgroundColor: 'lightyellow', color: 'black' }}>
                            ПЛАН
                        </th>
                        <th
                            className="td_num"
                            onClick={() => requestSort('averageCumulativeRevenue')}
                            title={REVENUE_HEADER_TOOLTIP}
                        >
                            Прогнозная<br />выручка {getSortIndicator('averageCumulativeRevenue')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('atStationToday')}>
                            Сколько<br />сегодня у<br />станка? {getSortIndicator('atStationToday')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('totalInCity')}>
                            Сколько<br />всего в<br />городе? {getSortIndicator('totalInCity')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('newWorkersToday')}>
                            Новые<br />сотруд-<br />ники {getSortIndicator('newWorkersToday')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('onMedicalToday')}>
                            На офрмл<br />м/о {getSortIndicator('onMedicalToday')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('leftFactoryToday')}>
                            Уволено<br />сегодня {getSortIndicator('leftFactoryToday')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('onVacationToday')}>
                            На м/вх<br />отпуске {getSortIndicator('onVacationToday')}
                        </th>
                        <th className="td_num" onClick={() => requestSort('requestToday')}>
                            Заявка<br />сегодня<br />(кол-во) {getSortIndicator('requestToday')}
                        </th>
                        <th className="td_num">Заявка<br />специальности</th>
                        <th className="td_sleeping_places" title="кол-во спальных мест">
                            <FaBed />
                        </th>
                        <th className="td_num">На отработке</th>
                        <th className="td_table">ТИП</th>
                        <th className="td_edit">Действия</th>
                    </tr>
                </thead>


                    <tbody>
                            {sortedReports.map((report, idx) => (

                            <tr key={report.id ? report.id : `report-${idx}`} hidden={hideNoReports && report.reportNotProvided}
                                className={`${report.reportNotProvided ? 'report-not-provided' : ''} `}
                            >
                                <td className="td_idx" style={{backgroundColor: `${colorOptionsFind(report.factory.cluster?.color)}`}}>{idx+1}</td>
                                <td className="td_idx">
                                    {report.factory.cluster?.name}

                                                                    {report.updatedAt && (
                                                                        <small className="td_updated_at">
                                                                          {new Date(report.updatedAt).toLocaleString('ru-RU', {
                                                                            day: '2-digit',
                                                                            month: '2-digit',
                                                                            year: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit',
                                                                          })}
                                                                          {report.timeZone && (
                                                                            <span className="td_timezone"> {formatTimeZone(report.timeZone)}</span>
                                                                          )}
                                                                        </small>
                                                                      )}
                                </td>
                                <td className="td_factory">
                                    <div className="td_factory_name" style={{ textDecoration: report.factory.deleted ? 'line-through' : 'none' }}>{report.factory.name}</div>
                                    {report.factory.master && (
                                        <>
                                            <div className="td_master_fio">Мастер - {report.factory.master?.fio}</div>
                                            <div className="td_master_phone">{report.factory.master?.phone}</div>
                                        </>
                                    )}
                                </td>

                                {/* Редактируемая ячейка для плана */}
                                <td
                                    className="td_plan"
                                    style={{ backgroundColor: 'lightyellow', maxWidth: '30px', textAlign: 'center' }}
                                    onClick={() => setEditingFactoryId(report.factory.id)}  // Включаем редактирование
                                >
                                    {editingFactoryId === report.factory.id ? (
                                        <input
                                            type="number"
                                            min="0"
                                            value={plans[report.factory.id] || ''}
                                            onChange={(e) => {
                                                e.target.value = Number(e.target.value);
                                                handlePlanInputChange(report.factory.id, e.target.value)
                                                }
                                            }
                                            onBlur={() => handleBlurOrEnter(report.factory.id, plans[report.factory.id])} // Сохранение при потере фокуса
                                            onKeyDown={(e) => handleKeyDown(e, report.factory.id, plans[report.factory.id])} // Сохранение при нажатии Enter
                                            autoFocus
                                        />
                                        ) : (
                                            plans[report.factory.id] || '—' // Отображаем значение или символ "—", если пусто
                                        )}
                                </td>
                                <td
                                    className="td_num"
                                    title={buildAverageCumulativeRevenueTooltip(report, formatRevenue(report.averageCumulativeRevenue))}
                                >
                                    {formatRevenue(report.averageCumulativeRevenue)}
                                </td>

                                {report.reportNotProvided ? (
                                    <>
                                        <td colSpan="8" className="td_report_not_provided">Отчет не предоставлен</td>
                                        {renderSleepingPlacesCell(report)}
                                        <td colSpan="2" className="td_report_not_provided"></td>
                                        <td className="td_edit">
                                            <button
                                                className="edit-report-btn"
                                                onClick={() => handleEditReport(report)}
                                                title="Создать отчет"
                                            >
                                                <FaEdit />
                                            </button>
                                        </td>
                                    </>
                                ) : (
                                <>
                                    <td className="td_num">{report.atStationToday}</td>                                    
                                    <td className="td_num">{report.totalInCity}</td>
                                    <td className="td_num">{report.newWorkersToday}</td>
                                    <td className="td_num">{report.onMedicalToday}</td>
                                    <td className="td_num">{report.leftFactoryToday}</td>
                                    <td className="td_num">{report.onVacationToday}</td>
                                    <td className="td_num">{report.requestToday}</td>
                                    <td className="td_text">{formatTextWithLineBreaks(report.requestTodayComment)}</td>
                                    {renderSleepingPlacesCell(report)}
                                    <td className="td_text">{formatTextWithLineBreaks(report.whoOtrabotkaToday)}</td>

                                    <td className="td_table">
                                        <table className="td_table">
                                        <tbody>
                                        <tr>
                                            <td>{formatTextWithLineBreaks(report.whoOnApproval)}</td>
                                            <th>согласование</th>
                                        </tr>

                                        <tr>
                                            <td>{formatTextWithLineBreaks(report.whoInCity)}</td>
                                            <th>в городе</th>
                                        </tr>

                                        <tr>
                                            <td>{report.whoAtFactory}</td>
                                            <th>выход</th>
                                        </tr>

                                        <tr>
                                            <td>{report.whoOnSickLeave}</td>
                                            <th>больн/отгул</th>
                                        </tr>

                                        <tr>
                                            <td>{report.whoReturnedFromVacation}</td>
                                            <th>возврат м/в</th>
                                        </tr>

                                        <tr>
                                            <td>{report.whoLeftForVacation}</td>
                                            <th>убыл в м/а</th>
                                        </tr>

                                        <tr>
                                            <td>{report.whoFiredToday}</td>
                                            <th>уволен</th>
                                        </tr>
                                        </tbody>
                                        </table>
                                    </td>
                                    <td className="td_edit">
                                        <button
                                            className="edit-report-btn"
                                            onClick={() => handleEditReport(report)}
                                            title="Редактировать отчет"
                                        >
                                            <FaEdit />
                                        </button>
                                    </td>
                                </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {reports.length > 1 && (
                                <>
                                    <tr className="daily-reports__totals-row">
                                            <td/>
                                            <td><strong>Итог</strong></td>
                                            <td/>
                                            <td/>
                                            <td
                                                className="td_num"
                                                title={buildAverageCumulativeRevenueTooltip(totals, formatRevenue(totals.averageCumulativeRevenue))}
                                            >
                                                {formatRevenue(totals.averageCumulativeRevenue)}
                                            </td>
                                            <td className="td_num" title='Сколько у станка?'>{totals.atStationToday}</td>
                                            <td className="td_num" title='Сколько всего в городе?'>{totals.totalInCity}</td>
                                            <td className="td_num" title='Новые сотрудники'>{totals.newWorkersToday}</td>
                                            <td className="td_num" title='На офрмл м/о'>{totals.onMedicalToday}</td>
                                            <td className="td_num" title='Уволено сегодня'>{totals.leftFactoryToday}</td>
                                            <td className="td_num" title='На м/вх отпуске'>{totals.onVacationToday}</td>
                                            <td className="td_num" title='Заявка сегодня (кол-во)'>{totals.requestToday}</td>
                                            <td/>
                                            <td/>
                                            <td/>
                                            <td/>
                                            <td/>
                                    </tr>
                                    {previousDayTotals && (
                                        <tr className="daily-reports__totals-row">
                                            <td/>
                                            <td><strong>Итог вчера</strong></td>
                                            <td/>
                                            <td/>
                                            <td
                                                className="td_num"
                                                title={buildAverageCumulativeRevenueTooltip(previousDayTotals, formatRevenue(previousDayTotals.averageCumulativeRevenue))}
                                            >
                                                {formatRevenue(previousDayTotals.averageCumulativeRevenue)}
                                            </td>
                                            <td className="td_num" title='Сколько у станка?'>{previousDayTotals.atStationToday}</td>
                                            <td className="td_num" title='Сколько всего в городе?'>{previousDayTotals.totalInCity}</td>
                                            <td className="td_num" title='Новые сотрудники'>{previousDayTotals.newWorkersToday}</td>
                                            <td className="td_num" title='На офрмл м/о'>{previousDayTotals.onMedicalToday}</td>
                                            <td className="td_num" title='Уволено сегодня'>{previousDayTotals.leftFactoryToday}</td>
                                            <td className="td_num" title='На м/вх отпуске'>{previousDayTotals.onVacationToday}</td>
                                            <td className="td_num" title='Заявка сегодня (кол-во)'>{previousDayTotals.requestToday}</td>
                                            <td/>
                                            <td/>
                                            <td/>
                                            <td/>
                                            <td/>
                                        </tr>
                                    )}
                                </>
                            )}
                    </tfoot>

                </table>
            </div>
            </div>
        </div>

        {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage(null)}
                />
        )}

        {editingReport && (
            <EditReportModal
                reportId={editingReport.id}
                factory={editingReport.factory}
                master={editingReport.master}
                initialReportDate={date}
                onClose={handleCloseEditModal}
                onSave={handleSaveReport}
            />
        )}
        </div>
    );
};

export default DailyReportsPage;
